import { getPriceSuggestion, GetPriceSuggestionRequestSchema, type editDrivingLesson } from "../../api/backoffice.api";
import type { AutovioCalendarEvent } from "../../model/autovioCalendarEvents";
import type { Money } from "../../model/Money";

export type LessonPriceDifference = {
  oldPrice: Money;
  oldPriceSuggestion: Money;
  newPriceSuggestion: Money;
};

export async function getLessonPriceDifference(
  event: AutovioCalendarEvent,
  edit: Parameters<typeof editDrivingLesson>[0],
): Promise<LessonPriceDifference | undefined> {
  if (
    event.type !== "DrivingLesson" ||
    !event.endedAt ||
    !event.price ||
    (event.price.amount <= 0 && !event.isManualPrice) ||
    (event.drivingLessonType === edit.drivingLessonType &&
      event.end.diff(event.start).as("minutes") === edit.end.diff(edit.start).as("minutes") &&
      (event.student.bookedTrainingId ?? edit.bookedTrainingId) === edit.bookedTrainingId)
  ) {
    return;
  }
  const request = (values: {
    drivingLessonType: string;
    instructorUid?: string;
    bookedTrainingId?: string;
    start: typeof event.start;
    end: typeof event.end;
    location: typeof event.startLocation;
  }) =>
    GetPriceSuggestionRequestSchema.parse({
      eventType: "DrivingLesson",
      studentUid: event.student.uid,
      ...values,
      drivingLessonType: ["unterweisungAmFahrzeug", "praktischeUnterweisung"].includes(values.drivingLessonType)
        ? "normal"
        : values.drivingLessonType,
    });
  const [oldSuggestion, newSuggestion] = await Promise.all([
    getPriceSuggestion(
      request({
        drivingLessonType: event.drivingLessonType,
        instructorUid: event.instructorId,
        bookedTrainingId: event.student.bookedTrainingId ?? edit.bookedTrainingId,
        start: event.start,
        end: event.end,
        location: event.startLocation,
      }),
    ),
    getPriceSuggestion(
      request({
        drivingLessonType: edit.drivingLessonType,
        instructorUid: edit.instructorId,
        bookedTrainingId: edit.bookedTrainingId,
        start: edit.start,
        end: edit.end,
        location: edit.startLocation,
      }),
    ),
  ]);
  if (Math.round(oldSuggestion.price.amount * 100) === Math.round(newSuggestion.price.amount * 100)) {
    return;
  }
  return {
    oldPrice: event.price,
    oldPriceSuggestion: oldSuggestion.price,
    newPriceSuggestion: newSuggestion.price,
  };
}
